import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, Button, Box, Typography, CircularProgress, Alert } from '@mui/material';
import NewOrder from '@/modules/orders/components/NewOrder';
import { getOrderById } from '@/modules/orders/redux/OrderCRUD';

interface OrderDetailModalProps {
  open: boolean;
  onClose: () => void;
  orderId: string | null;
  orderCode?: string;
}

export default function OrderDetailModal({
  open,
  onClose,
  orderId,
  orderCode
}: OrderDetailModalProps) {
  const [orderData, setOrderData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && orderId) {
      fetchOrderData();
    }
  }, [open, orderId]);

  const fetchOrderData = async () => {
    if (!orderId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getOrderById(orderId);
      setOrderData(response.data);
    } catch (err) {
      console.error('Error fetching order data:', err);
      setError('Không thể tải dữ liệu đơn hàng');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOrderData(null);
    setError(null);
    onClose();
  };

  const handleSave = async (orderData: any, orderId?: string) => {
    // This is a read-only modal, so we don't implement save functionality
    console.log('Save not implemented in read-only modal');
  };

  const handleUpdate = async (orderData: any, orderId?: string) => {
    // This is a read-only modal, so we don't implement update functionality
    console.log('Update not implemented in read-only modal');
  };

  const handleCancel = () => {
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Chi tiết đơn hàng {orderCode || orderId}
          </Typography>
          <Button onClick={handleClose} color="primary">
            Đóng
          </Button>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {orderData && !loading && (
          <NewOrder
            order={orderData}
            isEditMode={true}
            readOnly={true}
            onSave={handleSave}
            onUpdate={handleUpdate}
            onCancel={handleCancel}
            loading={loading}
            error={error}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}